import React, { useEffect, useState } from 'react';
import { DollarSign, CreditCard, Banknote, RefreshCw, Printer, Calendar } from 'lucide-react';
import { ArqueoCajaOperador } from '../../types/acreditacion.types';
import { acreditacionApi } from '../../services/acreditacionApi';

export const ArqueoCajaPanel: React.FC = () => {
  const [arqueo, setArqueo] = useState<ArqueoCajaOperador | null>(null);
  const [loading, setLoading] = useState(true);

  const cargarArqueo = async () => {
    try {
      setLoading(true);
      const data = await acreditacionApi.obtenerArqueoCaja();
      setArqueo(data);
    } catch (err) {
      console.error('Error cargando arqueo:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarArqueo();
  }, []);

  const handleImprimir = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-slate-400">
        <div className="w-10 h-10 border-4 border-brand-cyan/30 border-t-brand-cyan rounded-full animate-spin mb-4"></div>
        <span>Calculando arqueo de caja...</span>
      </div>
    );
  }

  const formatCurrency = (val: number) => {
    return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(val);
  };

  return (
    <div className="card-blueprint rounded-3xl p-6 sm:p-8 max-w-3xl mx-auto shadow-2xl space-y-6">
      {/* Header del Panel */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pb-6 border-b border-slate-700/50">
        <div>
          <h3 className="text-xl font-extrabold text-white">Arqueo de Caja del Operador</h3>
          <p className="text-xs text-slate-400 flex items-center gap-1.5 mt-1">
            <Calendar className="w-3.5 h-3.5 text-brand-cyan" />
            <span>Fecha: {arqueo?.fecha ? new Date(arqueo.fecha).toLocaleDateString('es-AR') : 'Hoy'}</span>
            <span className="mx-1">•</span>
            <span>Operador: {arqueo?.usuarioCajero || 'Cajero'}</span>
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={cargarArqueo}
            className="p-2.5 rounded-xl bg-brand-surface hover:bg-brand-navy text-slate-300 hover:text-white border border-brand-cyan/20 transition-colors"
            title="Recalcular"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <button
            onClick={handleImprimir}
            className="px-4 py-2.5 rounded-xl bg-brand-navy hover:bg-brand-surface text-brand-sky border border-brand-cyan/40 text-xs font-bold flex items-center gap-2 transition-colors"
          >
            <Printer className="w-4 h-4" />
            <span>Imprimir Cierre</span>
          </button>
        </div>
      </div>

      {/* Cards de Totales */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {/* Total General */}
        <div className="p-5 rounded-2xl bg-brand-surface/80 border border-brand-cyan/40 shadow-glow-cyan flex flex-col justify-between">
          <div className="flex items-center justify-between text-brand-sky mb-2">
            <span className="text-xs font-bold uppercase tracking-wider">Total Recaudado</span>
            <DollarSign className="w-5 h-5 text-brand-cyan" />
          </div>
          <div className="text-2xl font-extrabold text-white">
            {formatCurrency(arqueo?.totalGeneral || 0)}
          </div>
          <span className="text-[11px] text-slate-400 mt-1">
            {arqueo?.cantidadEntradasEmitidas || 0} entradas emitidas
          </span>
        </div>

        {/* Total Efectivo */}
        <div className="p-5 rounded-2xl bg-brand-surface/50 border border-slate-700/50 flex flex-col justify-between">
          <div className="flex items-center justify-between text-slate-300 mb-2">
            <span className="text-xs font-semibold uppercase tracking-wider">Efectivo</span>
            <Banknote className="w-5 h-5 text-emerald-400" />
          </div>
          <div className="text-2xl font-extrabold text-emerald-400">
            {formatCurrency(arqueo?.totalEfectivo || 0)}
          </div>
          <span className="text-[11px] text-slate-400 mt-1">En mano de caja</span>
        </div>

        {/* Total POS Mercado Pago */}
        <div className="p-5 rounded-2xl bg-brand-surface/50 border border-slate-700/50 flex flex-col justify-between">
          <div className="flex items-center justify-between text-slate-300 mb-2">
            <span className="text-xs font-semibold uppercase tracking-wider">POS Mercado Pago</span>
            <CreditCard className="w-5 h-5 text-brand-sky" />
          </div>
          <div className="text-2xl font-extrabold text-brand-sky">
            {formatCurrency(arqueo?.totalPosMercadoPago || 0)}
          </div>
          <span className="text-[11px] text-slate-400 mt-1">Lote POS verificado</span>
        </div>
      </div>

      {/* Detalle por Medio de Pago */}
      <div className="space-y-3 pt-4">
        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-300">
          Desglose por Terminal y Medio
        </h4>

        {arqueo?.detalleMediosPago && arqueo.detalleMediosPago.length > 0 ? (
          <div className="divide-y divide-slate-800 rounded-2xl border border-brand-cyan/20 overflow-hidden bg-brand-dark/50">
            {arqueo.detalleMediosPago.map((item) => (
              <div key={item.idMedioPago} className="p-4 flex items-center justify-between text-sm">
                <div className="flex items-center gap-3">
                  {item.codigoMedioPago.includes('POS') ? (
                    <CreditCard className="w-4 h-4 text-brand-sky" />
                  ) : (
                    <Banknote className="w-4 h-4 text-emerald-400" />
                  )}
                  <div>
                    <div className="font-bold text-white">{item.nombreMedioPago}</div>
                    <div className="text-xs text-slate-400">{item.cantidadOperaciones} transacciones</div>
                  </div>
                </div>
                <div className="font-mono font-extrabold text-white text-base">
                  {formatCurrency(item.montoTotal)}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-6 text-xs text-slate-400 bg-brand-surface/20 rounded-2xl">
            No se han registrado ventas en la sesión de caja actual.
          </div>
        )}
      </div>
    </div>
  );
};
